import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  CheckCircle,
  XCircle,
  Eye,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/DataTable";
import { Card, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { VolunteerForm } from "./VolunteerForm";
import { VolunteerDetail } from "./VolunteerDetail";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Volunteer {
  id: number;
  agency_id: number;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  emergency_contact: string | null;
  skills: string | null;
  interests: string | null;
  is_active: number;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VolunteerList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState<Volunteer | null>(null);
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);

  const { data: volunteers, isLoading } = useQuery<Volunteer[]>({
    queryKey: ["volunteers"],
    queryFn: async () => {
      const res = await api.get<{ data: Volunteer[] }>("/volunteers");
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/volunteers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api.put(`/volunteers/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
    },
  });

  function handleEdit(volunteer: Volunteer) {
    setEditingVolunteer(volunteer);
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setEditingVolunteer(null);
  }

  if (selectedVolunteer) {
    return (
      <VolunteerDetail
        volunteer={selectedVolunteer}
        onBack={() => setSelectedVolunteer(null)}
      />
    );
  }

  const columns: ColumnDef<Volunteer, unknown>[] = [
    {
      accessorFn: (row) => `${row.last_name}, ${row.first_name}`,
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <button
          className="font-medium text-primary hover:underline"
          onClick={() => setSelectedVolunteer(row.original)}
        >
          {row.original.last_name}, {row.original.first_name}
        </button>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ getValue }) => getValue() ?? "--",
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ getValue }) => getValue() ?? "--",
    },
    {
      accessorKey: "skills",
      header: "Skills",
      cell: ({ getValue }) => {
        const val = getValue() as string | null;
        return val ? <span className="text-xs">{val}</span> : "--";
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <button
          onClick={() =>
            toggleMutation.mutate({
              id: row.original.id,
              isActive: !row.original.is_active,
            })
          }
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
            row.original.is_active
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-600",
          )}
        >
          {row.original.is_active ? (
            <>
              <CheckCircle className="h-3 w-3" /> Active
            </>
          ) : (
            <>
              <XCircle className="h-3 w-3" /> Inactive
            </>
          )}
        </button>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedVolunteer(row.original)}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(row.original)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm("Delete this volunteer? This cannot be undone.")) {
                deleteMutation.mutate(row.original.id);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Volunteer Directory</h2>
          <p className="text-sm text-muted-foreground">
            Manage volunteer records and contact information
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            handleCloseForm();
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New Volunteer
        </Button>
      </div>

      {showForm && (
        <VolunteerForm volunteer={editingVolunteer} onClose={handleCloseForm} />
      )}

      {!isLoading && (volunteers ?? []).length === 0 && !showForm && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No volunteers added yet.
            </p>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={volunteers ?? []}
        searchColumn="name"
        searchPlaceholder="Search volunteers..."
        isLoading={isLoading}
      />
    </div>
  );
}
